<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Semesters Management
                </h1>
                <button
                    @click="openModal"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Semester
                </button>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by name..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.is_active"
                        @change="fetchSemesters"
                    />

                    <!-- Academic Year Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.academic_year_id"
                            :options="academicYearOptions"
                            label="Academic Year"
                            placeholder="All Academic Years"
                            option-label="name"
                            option-value="id"
                            @update:model-value="fetchSemesters"
                        />
                    </div>

                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="semesterStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div></div>
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="semesterStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="semesterStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ semesterStore.error }}
                        </p>
                        <button
                            @click="semesterStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!semesterStore.loading && !semesterStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Code
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Name
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Academic Year
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Start Date
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    End Date
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="semester in semesterStore.semesters"
                                :key="semester.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
                                    >
                                        {{ semester.code || "-" }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ semester.name }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ semester.academic_year?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ formatDate(semester.start_date) }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ formatDate(semester.end_date) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="semester.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                    >
                                        Inactive
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editSemester(semester)"
                                        title="Edit Semester"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        v-if="!semester.is_active"
                                        @click="setActive(semester)"
                                        title="Set Active"
                                        class="inline-flex items-center justify-center p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors mr-2"
                                    >
                                        <CheckCircle class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(semester)"
                                        title="Delete Semester"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="semesterStore.semesters.length === 0"
                    class="text-center py-12"
                >
                    <Calendar class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No semesters found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new semester.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Semester
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (semesterStore.meta?.total || 0) >
                    (semesterStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="semesterStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            semesterStore.meta?.current_page ===
                            semesterStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                paginationFrom
                            }}</span>
                            to
                            <span class="font-medium">{{ paginationTo }}</span>
                            of
                            <span class="font-medium">{{
                                paginationTotal
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    semesterStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === semesterStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    semesterStore.meta?.current_page ===
                                    semesterStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Semester Modal -->
            <SemesterModal
                :show="showModal"
                :semester="selectedSemester"
                :is-editing="isEditing"
                :academic-years="academicYears"
                @close="closeModal"
                @saved="handleSave"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                :show="showDeleteModal"
                title="Delete Semester"
                :message="
                    semesterToDelete
                        ? `Are you sure you want to delete ${semesterToDelete.name}? This action cannot be undone.`
                        : ''
                "
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useSemesterStore } from "@/stores/semester";
import { useAcademicYearStore } from "@/stores/academicYear";
import SearchableSelect from "@/components/SearchableSelect.vue";
import SemesterModal from "@/components/SemesterModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import academicYearApi from "@/api/academicYear";
import type { Semester } from "@/api/semester";
import type { AcademicYear } from "@/api/academicYear";
import {
    Plus,
    RotateCcw,
    Pencil,
    CheckCircle,
    Trash2,
    Calendar,
} from "@lucide/vue";

const semesterStore = useSemesterStore();
const academicYearStore = useAcademicYearStore();
// State
const searchQuery = ref("");
const filters = ref({
    is_active: undefined as boolean | undefined,
    academic_year_id: undefined as string | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedSemester = ref<Semester | null>(null);
const isEditing = ref(false);
const semesterToDelete = ref<Semester | null>(null);

// Computed
const paginationFrom = computed(() => {
    return semesterStore.meta?.from ?? 0;
});

const paginationTo = computed(() => {
    return semesterStore.meta?.to ?? 0;
});

const paginationTotal = computed(() => {
    return semesterStore.meta?.total ?? 0;
});

const academicYears = computed(() => academicYearStore.academicYears);

// Options for Academic Year filter
const academicYearOptions = computed(() => {
    const options = [
        { id: undefined as string | undefined, name: "All Academic Years" },
    ];
    academicYears.value.forEach((year: AcademicYear) => {
        options.push({ id: year.id, name: year.name });
    });
    return options;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.is_active !== undefined ||
        filters.value.academic_year_id !== undefined ||
        semesterStore.page !== 1 ||
        semesterStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    semesterStore.perPage = value;
    semesterStore.page = 1;
    fetchSemesters();
};

const fetchSemesters = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await semesterStore.fetchSemesters(searchFilters);
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { is_active: undefined, academic_year_id: undefined };
    semesterStore.setPage(1);
    semesterStore.setPerPage(10);
    fetchSemesters();
};

const debouncedSearch = useDebounce(fetchSemesters, 300);

const openModal = () => {
    selectedSemester.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any previous errors when opening modal
    semesterStore.clearError();
};

const editSemester = (semester: Semester) => {
    selectedSemester.value = semester;
    isEditing.value = true;
    showModal.value = true;
    // Clear any previous errors when opening modal
    semesterStore.clearError();
};

const closeModal = () => {
    showModal.value = false;
    selectedSemester.value = null;
    isEditing.value = false;
};

const confirmDelete = (semester: Semester) => {
    semesterToDelete.value = semester;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    semesterToDelete.value = null;
};

const handleSave = async () => {
    const wasEditing = isEditing.value;
    closeModal();
    await semesterStore.fetchSemesters();
    showNotification(
        wasEditing
            ? "Semester updated successfully"
            : "Semester created successfully",
        "success",
    );
    semesterStore.clearError();
};

const handleDelete = async () => {
    try {
        if (semesterToDelete.value) {
            await semesterStore.deleteSemester(semesterToDelete.value.id);
            showNotification("Semester deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete semester", "error");
    }
};

const setActive = async (semester: Semester) => {
    try {
        await semesterStore.setActiveSemester(semester.id);
        showNotification("Semester set as active successfully", "success");
    } catch (error) {
        showNotification("Failed to set semester as active", "error");
    }
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
};

// Pagination
const previousPage = () => {
    if (semesterStore.meta && semesterStore.meta.current_page > 1) {
        semesterStore.setPage(semesterStore.meta.current_page - 1);
    }
};

const nextPage = () => {
    if (
        semesterStore.meta &&
        semesterStore.meta.current_page < semesterStore.meta.last_page
    ) {
        semesterStore.setPage(semesterStore.meta.current_page + 1);
    }
};

const goToPage = (page: number) => {
    semesterStore.setPage(page);
};

const visiblePages = computed(() => {
    if (!semesterStore.meta) return [1];
    const current = semesterStore.meta.current_page;
    const last = semesterStore.meta.last_page;
    const pages: (number | string)[] = [];
    const range = 2;
    let start = Math.max(1, current - range);
    let end = Math.min(last, current + range);
    if (start > 1) pages.push(1);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < last - 1) pages.push("...");
    if (end < last) pages.push(last);
    return pages;
});

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(async () => {
    // Fetch academic years first for the dropdown
    await academicYearStore.fetchAcademicYears();
    // Then fetch semesters
    fetchSemesters();
});
</script>
